import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slide", "indicator", "previous", "next", "indicators"]
  static values = { 
    currentIndex: Number,
    type: { type: String, default: 'photos' } // 'photos' ou 'sleeps'
  }

  connect() {
    this.carouselId = this.element.id || `carousel-${Math.random().toString(36).substring(2, 9)}`;
    console.log(`Carousel connected: ${this.carouselId}`);
    
    this.currentIndexValue = this.currentIndexValue || 0;
    this.touchStartX = 0;
    this.touchEndX = 0;

    if (this.slideTargets.length <= 1) {
      if (this.hasPreviousTarget) this.previousTarget.classList.add("hidden");
      if (this.hasNextTarget) this.nextTarget.classList.add("hidden");
      if (this.hasIndicatorsTarget) this.indicatorsTarget.classList.add("hidden");
    }

    this.updateSlider();
    this.setupTouchEvents();
  }

  setupTouchEvents() {
    if (this.typeValue === 'sleeps') {
      this.handleTouchStartBound = this.handleTouchStart.bind(this);
      this.handleTouchMoveBound = this.handleTouchMove.bind(this);
      this.handleTouchEndBound = this.handleTouchEnd.bind(this);
      
      this.element.addEventListener('touchstart', this.handleTouchStartBound, { passive: true });
      this.element.addEventListener('touchmove', this.handleTouchMoveBound, { passive: true });
      this.element.addEventListener('touchend', this.handleTouchEndBound, { passive: true });
    }
  }

  handleTouchStart(event) {
    this.touchStartX = event.touches[0].clientX;
    this.touchMoveX = this.touchStartX;
    this.slideStartTime = new Date();
  }

  handleTouchMove(event) {
    if (this.typeValue !== 'sleeps') return;
    
    this.touchMoveX = event.touches[0].clientX;
    const deltaX = this.touchMoveX - this.touchStartX;
    this.moveSlides(deltaX);
  }

  handleTouchEnd() {
    const deltaX = this.touchMoveX - this.touchStartX;
    const deltaTime = new Date() - this.slideStartTime;
    const velocity = Math.abs(deltaX) / deltaTime;

    if (Math.abs(deltaX) > 50 || velocity > 0.5) {
      if (deltaX > 0) {
        this.previous();
      } else {
        this.next();
      }
    } else {
      this.updateSlider();
    }
  }

  moveSlides(deltaX) {
    if (this.typeValue !== 'sleeps') return;

    const container = this.slideTargets[0].parentElement;
    const currentTranslate = -100 * this.currentIndexValue;
    const additionalTranslate = (deltaX / container.offsetWidth) * 100;
    container.style.transform = `translateX(${currentTranslate + additionalTranslate}%)`;
  }

  next() {
    if (this.currentIndexValue < this.slideTargets.length - 1) {
      this.currentIndexValue++;
      this.updateSlider();
    }
  }

  previous() {
    if (this.currentIndexValue > 0) {
      this.currentIndexValue--;
      this.updateSlider();
    }
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index);
    if (index >= 0 && index < this.slideTargets.length) {
      this.currentIndexValue = index;
      this.updateSlider();
    }
  }

  updateSlider() {
    console.log(`Updating slider to index ${this.currentIndexValue} in carousel: ${this.carouselId}`);
    
    if (this.typeValue === 'photos') {
      // Gestion du carousel de photos avec opacity
      this.slideTargets.forEach((slide, index) => {
        if (index === this.currentIndexValue) {
          slide.classList.remove('opacity-0', 'absolute');
          slide.classList.add('opacity-100');
        } else {
          slide.classList.add('opacity-0', 'absolute');
          slide.classList.remove('opacity-100');
        }
      });
    } else {
      // Gestion du carousel d'hébergements avec transform
      const container = this.slideTargets[0].parentElement;
      container.style.transition = 'transform 300ms ease-in-out';
      container.style.transform = `translateX(-${this.currentIndexValue * 100}%)`;
      
      setTimeout(() => {
        container.style.transition = '';
      }, 300);
    }

    if (this.hasIndicatorsTarget) {
      this.indicatorsTarget.innerHTML = '';
      
      const totalSlides = this.slideTargets.length;
      
      if (this.typeValue === 'photos') {
        // Pour le carousel de photos, afficher tous les indicateurs
        for (let i = 0; i < totalSlides; i++) {
          this.createIndicator(i, 'w-3 h-3');
        }
      } else {
        // Pour le carousel d'hébergements, limiter le nombre d'indicateurs sur mobile
        const isMobile = window.innerWidth < 768;
        const maxIndicators = isMobile ? 5 : totalSlides;
        
        if (totalSlides <= maxIndicators) {
          for (let i = 0; i < totalSlides; i++) {
            this.createIndicator(i, 'w-2 h-2');
          }
        } else {
          const currentIndex = this.currentIndexValue;
          const lastIndex = totalSlides - 1;
          
          this.createIndicator(0, 'w-2 h-2');
          
          if (currentIndex > 1) {
            this.createEllipsis();
          }
          
          for (let i = Math.max(1, currentIndex - 1); i <= Math.min(lastIndex - 1, currentIndex + 1); i++) {
            this.createIndicator(i, 'w-2 h-2');
          }
          
          if (currentIndex < lastIndex - 2) {
            this.createEllipsis();
          }
          
          if (lastIndex > 0) {
            this.createIndicator(lastIndex, 'w-2 h-2');
          }
        }
      }
    }
  }

  createIndicator(index, sizeClasses) {
    const indicator = document.createElement('button');
    indicator.type = 'button';
    indicator.dataset.carouselTarget = 'indicator';
    indicator.dataset.action = 'click->carousel#goToSlide';
    indicator.dataset.index = index;
    indicator.className = `${sizeClasses} rounded-full mx-1`;
    indicator.style.backgroundColor = index === this.currentIndexValue ? '#1F2648' : '#FAB313';
    this.indicatorsTarget.appendChild(indicator);
  }

  createEllipsis() {
    const ellipsis = document.createElement('span');
    ellipsis.className = 'mx-1 text-gray-400 text-sm leading-none';
    ellipsis.textContent = '...';
    this.indicatorsTarget.appendChild(ellipsis);
  }

  disconnect() {
    if (this.handleTouchStartBound) {
      this.element.removeEventListener('touchstart', this.handleTouchStartBound);
    }
    if (this.handleTouchMoveBound) {
      this.element.removeEventListener('touchmove', this.handleTouchMoveBound);
    }
    if (this.handleTouchEndBound) {
      this.element.removeEventListener('touchend', this.handleTouchEndBound);
    }
  }
}
